/**
 * InvitationAccept - Unified page for accepting event invitations.
 *
 * Handles BOTH:
 *   1. Delegate invites (legacy `db.invites`)  → redirect to /delegate
 *   2. Admin team invites (`db.invitations`)   → log them in, redirect via /auth/landing
 *
 * The backend now returns `is_team_invite: true` + an `access_token` on team
 * accept so the user is auto-logged-in and never bounces through /login.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Loader2, Calendar, MapPin, Ticket, AlertCircle, CheckCircle2, PartyPopper, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/store';
import afterLogin from '@/lib/afterLogin';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvitationAccept = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    password: '',
    confirm_password: '',
    company: '',
    title: '',
    phone: '',
  });

  useEffect(() => {
    fetchInvitation();
  }, [token]);

  const fetchInvitation = async () => {
    try {
      const response = await fetch(`${API_URL}/api/invitations/${token}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Invalid or expired invitation');
      }

      setInvitation(data);
      setFormData(prev => ({
        ...prev,
        first_name: data.first_name || '',
        last_name: data.last_name || '',
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/invitations/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.first_name,
          last_name: formData.last_name,
          password: formData.password,
          company: formData.company,
          title: formData.title,
          phone: formData.phone,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to accept invitation');
      }

      setSuccess(true);

      // ----- TEAM/ADMIN INVITE PATH -----
      // Backend signals admin invites with `is_team_invite: true` and includes
      // an `access_token` so we can log the user in directly. We then defer
      // the actual destination URL to /api/auth/landing so the freshly-onboarded
      // user always lands in the workspace they were invited to (event admin
      // dashboard, scanner page, school portal, etc.) — never on /delegate.
      if (data.is_team_invite && data.access_token) {
        const adminUser = {
          id: data.admin_id,
          email: data.email,
          name: `${formData.first_name} ${formData.last_name}`.trim() || data.email,
          role: data.role,
          event_id: data.event_id || null,
          is_super_admin: false,
        };
        setAuth(data.access_token, adminUser, 'admin', null, []);
        toast.success("You're in! Redirecting to your workspace…");
        // Tiny delay so the success card flashes long enough to be readable.
        setTimeout(() => {
          afterLogin({ navigate, userType: 'admin', tenantId: null, redirect: '' });
        }, 800);
        return;
      }

      // ----- LEGACY DELEGATE INVITE PATH (unchanged) -----
      if (data.access_token) {
        localStorage.setItem('eventflow_token', data.access_token);
      }

      toast.success('Invitation accepted successfully!');

      // Redirect based on payment requirement
      setTimeout(() => {
        if (data.requires_payment) {
          navigate(`/events/${data.event_slug}?payment=pending&delegate=${data.id}`);
        } else {
          navigate('/delegate');
        }
      }, 2000);

    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            <span className="ml-3 text-muted-foreground">Loading invitation...</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <CardTitle className="text-red-700">Invalid Invitation</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Button variant="outline" onClick={() => navigate('/')}>
              Go to Homepage
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <PartyPopper className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <CardTitle className="text-green-700">Welcome Aboard!</CardTitle>
            <CardDescription>
              Your invitation has been accepted. Redirecting you...
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-green-600" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Badge className="bg-indigo-100 text-indigo-800 mb-4">
            ✨ You're Invited!
          </Badge>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {invitation?.event?.name}
          </h1>
          {invitation?.message && (
            <p className="text-gray-600 italic">"{invitation.message}"</p>
          )}
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Event Info Card */}
          <Card className="bg-white/80 backdrop-blur">
            <CardHeader>
              <CardTitle className="text-lg">Event Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {invitation?.event?.start_date && (
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-indigo-600" />
                  <span>{new Date(invitation.event.start_date).toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</span>
                </div>
              )}
              {invitation?.event?.venue?.name && (
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-indigo-600" />
                  <span>
                    {invitation.event.venue.name}
                    {invitation.event.venue.city && `, ${invitation.event.venue.city}`}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-3">
                <Ticket className="h-5 w-5 text-indigo-600" />
                <div>
                  <span className="font-medium">{invitation?.tier?.name}</span>
                  {invitation?.tier?.price > 0 ? (
                    <span className="text-green-600 ml-2">${invitation.tier.price}</span>
                  ) : (
                    <Badge variant="secondary" className="ml-2">Free</Badge>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Registration Form Card */}
          <Card className="bg-white/80 backdrop-blur">
            <CardHeader>
              <CardTitle className="text-lg">Complete Registration</CardTitle>
              <CardDescription>
                Fill in your details to accept the invitation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={invitation?.email}
                    disabled
                    className="bg-gray-50"
                  />
                </div>

                <div>
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <Label htmlFor="confirm_password">Confirm Password *</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={formData.confirm_password}
                    onChange={(e) => setFormData(prev => ({ ...prev, confirm_password: e.target.value }))}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="company">Company</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Accepting...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Accept Invitation
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {invitation?.tier?.price > 0 && (
          <Alert className="mt-6 bg-amber-50 border-amber-200">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              After accepting, you'll be redirected to complete payment of ${invitation.tier.price}.
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};

export default InvitationAccept;
